class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = webvista.debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
    const facetForm = this.querySelector("form");
    facetForm.addEventListener("change", this.debouncedOnSubmit.bind(this));
    
    // Guard against any native form submission (mobile keyboards, a11y tools, stray submit-type controls) causing a full page reload.
    facetForm.addEventListener("submit", (event) => {
      event.preventDefault();
    });

    this.addEventListener("keyup", webvista.onKeyUpEscape);
  }

  /* ====== Listen for browser history change events ====== */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  /* ====== Re-render the page ====== */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections(); // Get the section to re-render (product grid)
    const contentContainer = document.getElementById("Product-Grid-Container").querySelector(".content-list");
    if (contentContainer) contentContainer.classList.add("loading");
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      // Determine whether to load cached content
      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, searchParams, updateURLHash) // Load cached content
        : FacetFiltersForm.renderSectionFromFetch(url, event, searchParams, updateURLHash); // Fetch fresh data
    }); // URL updates have been moved into each render method and are executed only after successful rendering
  }

  /* ====== Asynchronously fetch search results ====== */
  static renderSectionFromFetch(url, event, searchParams, updateURLHash) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.statusText}`,
          );
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        const parsedDocument = new DOMParser().parseFromString(
          html,
          "text/html",
        );
        if (!parsedDocument.getElementById("Product-Grid-Container")) {
          throw new Error(
            "Product-Grid-Container missing from section response",
          );
        }
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFacets(html, event); // Render filters
        FacetFiltersForm.renderProductGridContainer(html); // Render the product grid
        FacetFiltersForm.renderProductCount(html); // Render the total number of matching products
        // Update the browser history only after rendering completes successfully
        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      })
      .catch((error) => {
        console.error("Search pagination rendering failed:", {
          error,
          url,
          searchParams,
        });
        if (window.webvista && typeof window.webvista.popToast === "function") {
          window.webvista.popToast(
            window.accessibilityStrings?.unknownError ||
              "Unable to load search results.",
              "error",
          );
        }
      })
      .finally(() => {
        document.querySelector("#Product-Grid-Container .content-list")?.classList.remove("loading");
      });
  }

  /* ====== Cache the fetched data ====== */
  static renderSectionFromCache(filterDataUrl, event, searchParams, updateURLHash) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFacets(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    document.querySelector("#Product-Grid-Container .content-list")?.classList.remove("loading");
  }

  /* ====== Render the product grid ====== */
  static renderProductGridContainer(html) {
    const targetProductContainer = document.getElementById("Product-Grid-Container");
    if (!targetProductContainer) return false;

    const parsedDocument = new DOMParser().parseFromString(html, "text/html");
    const sourceProductContainer = parsedDocument.getElementById("Product-Grid-Container");
    if (!sourceProductContainer) return false;

    targetProductContainer.innerHTML = sourceProductContainer.innerHTML;
    
    if (typeof window.initDynamicPrices === "function") {
      window.initDynamicPrices();
    }

    if (window.yotpoWidgetsContainer) {
      window.yotpoWidgetsContainer.initWidgets();
    }

    if (window.FlairApp && typeof window.FlairApp.init === "function") {
      window.FlairApp.init();
    }

    if (typeof syncPriceRangeFromURL === "function") {
      syncPriceRangeFromURL();
    }

    if (typeof initProductSliderCollection === "function") {
      initProductSliderCollection();
    }
    
    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger(targetProductContainer);
    }

    webvista.initLazyImages();
    webvista.initTooltips();

    if (typeof window.initRatingPlaceholders === "function") {
      window.initRatingPlaceholders();
    }
  }

  /* ====== Update the total number of matching products :: Handles both vertical layout and horizontal layout with drawer ====== */
  static renderProductCount(html) {
    const countIds = [
      "ProductCountVertical",
      "Product-Count-Horizontal-And-Drawer",
    ];
    const htmlDocument = new DOMParser().parseFromString(html, "text/html");
    countIds.forEach((id) => {
      const source = htmlDocument.getElementById(id);
      const target = document.getElementById(id);
      if (source && target) target.outerHTML = source.outerHTML;
    });
  }

  /* ====== Render filters and sorting ====== */
  static renderFacets(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const selectors = [
      "#Facet-Horizontal-Wrapper",
      "#Facet-Vertical-Wrapper",
      "#Facet-Drawer-Opener-Wrapper",
      "#Facet-Drawer-Wrapper",
    ];
    selectors.forEach((selector) => {
      const target = document.querySelector(selector);
      const source = parsedHTML.querySelector(selector);
      if (!target || !source) return;

      if (event && target.contains(event.target)) {
        const matchesIndex = (element) => {
          const facetItem = event ? event.target.closest(".facet-item") : undefined;
          return facetItem ? element.dataset.index === facetItem.dataset.index : false;
        };
        // Render active filters
        const facetActiveSource = source.querySelector(".facets-active");
        const facetActiveTarget = target.querySelector(".facets-active");
        if (facetActiveSource && facetActiveTarget)
          facetActiveTarget.innerHTML = facetActiveSource.innerHTML;

        // Render filters
        const facets = Array.from(source.querySelectorAll(".facet-item"));
        // Non-active filters
        facets
          .filter((facet) => !matchesIndex(facet))
          .forEach((facet) => {
            if (!facet.hasAttribute("data-index")) return;

            const targetFacet = target.querySelector(`.facet-item[data-index="${facet.getAttribute("data-index")}"]`);
            if (targetFacet) targetFacet.outerHTML = facet.outerHTML;
          });

        // Active filter
        const currentFacet = facets.find((facet) => matchesIndex(facet));
        if (!currentFacet) return;

        const targetFacet = target.querySelector(`.facet-item[data-index="${currentFacet.getAttribute("data-index")}"]`);
        if (targetFacet) {
          const targetSelected = targetFacet.querySelector(".facets-selected");
          const currentSelected = currentFacet.querySelector(".facets-selected");
          if (targetSelected && currentSelected) {
            targetSelected.replaceWith(currentSelected);
          }
        }
      } else {
        if (source) target.innerHTML = source.innerHTML;
      }
    });
  }

  /* ====== Update the browser history without triggering a page reload ====== */
  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  /* ====== Get the sections that need to be re-rendered ====== */
  static getSections() {
    return [
      {
        section: document.getElementById("Paginate-Content").dataset.section,
      },
    ];
  }

  /* ====== Build request parameters ====== */
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  /* ====== Submit the form ======*/
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /* ====== Filter and sorting event handler ====== */
  onSubmitHandler(event) {
    event.preventDefault();
    const form = this.querySelector("form");
    this.onSubmitForm(this.createSearchParams(form), event); // Combine query parameters from all forms and submit
  }

  /* ====== Handle click events for selected options ====== */
  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf("?") === -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url, null);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);

FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.rangeMin = this.querySelector("input.range-min");
    this.rangeMax = this.querySelector("input.range-max");
    this.inputMin = this.querySelector("input.input-min");
    this.inputMax = this.querySelector("input.input-max");

    if (!this.rangeMin || !this.rangeMax) return;

    // Debounce state
    this.debounceMs = 1000;
    this.emitTimer = null;
    this.boundOnRangeMinChange = this.onRangeMinChange.bind(this);
    this.boundOnRangeMaxChange = this.onRangeMaxChange.bind(this);
    this.boundOnInputMinChange = this.onInputMinChange.bind(this);
    this.boundOnInputMaxChange = this.onInputMaxChange.bind(this);
    this.boundStopEmit = this.stopEmit.bind(this);
    this.initBlockNativeChange(); // Prevent the native input change event from bubbling and dispatch it manually instead
    this.rangeMin.addEventListener("input", this.boundOnRangeMinChange);
    this.rangeMax.addEventListener("input", this.boundOnRangeMaxChange);

    if (this.inputMin) {
      this.inputMin.addEventListener("input", this.boundStopEmit); // Do not emit if there is user input
      this.inputMin.addEventListener("change", this.boundOnInputMinChange);
    }

    if (this.inputMax) {
      this.inputMax.addEventListener("input", this.boundStopEmit);
      this.inputMax.addEventListener("change", this.boundOnInputMaxChange);
    }

    this.setProgressMin();
    this.setProgressMax();
  }

  disconnectedCallback() {
    if (this.rangeMin && this.boundOnRangeMinChange)
      this.rangeMin.removeEventListener("input", this.boundOnRangeMinChange);
    if (this.rangeMax && this.boundOnRangeMaxChange)
      this.rangeMax.removeEventListener("input", this.boundOnRangeMaxChange);
    if (this.inputMin && this.boundOnInputMinChange)
      this.inputMin.removeEventListener("change", this.boundOnInputMinChange);
    if (this.inputMax && this.boundOnInputMaxChange)
      this.inputMax.removeEventListener("change", this.boundOnInputMaxChange);

    if (this.boundStopEmit) {
      if (this.inputMin)
        this.inputMin.removeEventListener("input", this.boundStopEmit);
      if (this.inputMax)
        this.inputMax.removeEventListener("input", this.boundStopEmit);
    }
    this.stopEmit(); // Clear the timer
    this.removeBlockNativeChange(); // Remove the capture-phase interception of the native change event
  }

  /* ====== Prevent the native input change event from bubbling during the capture phase ====== */
  initBlockNativeChange() {
    this.boundBlockNativeChange = (e) => e.stopPropagation();
    [this.rangeMin, this.rangeMax, this.inputMin, this.inputMax]
      .filter(Boolean)
      .forEach((el) =>
        el.addEventListener("change", this.boundBlockNativeChange),
      );
  }

  /* ====== Disable the capture-phase interception of the native change event ====== */
  removeBlockNativeChange() {
    [this.rangeMin, this.rangeMax, this.inputMin, this.inputMax]
      .filter(Boolean)
      .forEach((el) =>
        el.removeEventListener("change", this.boundBlockNativeChange),
      );
  }

  /* ====== Schedule a change event dispatch ====== */
  scheduleEmit() {
    this.stopEmit(); // Clear the existing timer
    this.emitTimer = setTimeout(() => {
      this.dispatchEvent(new Event("change", { bubbles: true }));
    }, this.debounceMs);
  }

  /* ====== Minimum range value changed ====== */
  onRangeMinChange(event) {
    const minVal = webvista.toNumber(this.rangeMin.value);
    const maxVal = webvista.toNumber(this.rangeMax.value, minVal);

    if (minVal >= maxVal) {
      this.rangeMin.value = String(maxVal);
    }

    if (this.inputMin) this.inputMin.value = this.rangeMin.value;

    this.setProgressMin();
    this.scheduleEmit();
  }

  /* ====== Handle maximum range value changes ====== */
  onRangeMaxChange(event) {
    const maxVal = webvista.toNumber(this.rangeMax.value);
    const minVal = webvista.toNumber(this.rangeMin.value, maxVal);

    if (maxVal <= minVal) {
      this.rangeMax.value = String(minVal);
    }

    if (this.inputMax) this.inputMax.value = this.rangeMax.value;
    this.setProgressMax();
    this.scheduleEmit();
  }

  /* ====== Prevent the change event from being dispatched ====== */
  stopEmit() {
    if (this.emitTimer) {
      clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
  }

  /* ====== Handle minimum input value changes ====== */
  onInputMinChange(event) {
    let value = webvista.toNumber(event.currentTarget.value);
    const min = webvista.toNumber(this.rangeMin.min);
    const max = webvista.toNumber(this.rangeMax.value);
    // Ensure the input value is within the allowed range
    value = webvista.clampNumber(value, min, max);

    this.rangeMin.value = String(value);
    event.currentTarget.value = String(value); // Update the input value if it was adjusted above

    this.setProgressMin();
    this.scheduleEmit();
  }

  /* ====== Handle maximum input value changes ====== */
  onInputMaxChange(event) {
    let value = webvista.toNumber(event.currentTarget.value);
    const min = webvista.toNumber(this.rangeMin.value);
    const max = webvista.toNumber(this.rangeMax.max);

    // Ensure the input value is within the allowed range
    value = webvista.clampNumber(value, min, max);

    this.rangeMax.value = String(value);
    event.currentTarget.value = String(value); // Update the input value if it was adjusted above

    this.setProgressMax();
    this.scheduleEmit();
  }

  /* ====== Adjust the minimum value slider ====== */
  setProgressMin() {
    const min = webvista.toNumber(this.rangeMin.min);
    const max = webvista.toNumber(this.rangeMin.max, min);
    const span = max - min;
    const cur = webvista.toNumber(this.rangeMin.value, min);
    this.style.setProperty("--start", `${((cur - min) * 100) / span}%`);
  }

  /* ====== Adjust the maximum value slider ====== */
  setProgressMax() {
    const min = webvista.toNumber(this.rangeMin.min);
    const max = webvista.toNumber(this.rangeMin.max, min);
    const span = max - min;
    const cur = webvista.toNumber(this.rangeMax.value, max);
    this.style.setProperty("--end", `${((cur - min) * 100) / span}%`);
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();

      if (event.code && event.code.toUpperCase() === "SPACE")
        this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);